"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

/**
 * Last-resort boundary for errors in the root layout itself.
 * Replaces the entire document, so it must own <html> / <body>.
 * Keep this file dependency-light — if THIS throws, Next falls back to its
 * opaque default and we lose the digest/message we need to debug.
 */
export default function GlobalError({
  error,
  reset,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  reset?: () => void;
  unstable_retry?: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
    console.error("[global-error]", error.digest ?? "(no digest)", error);
  }, [error]);

  const retry = () => {
    if (unstable_retry) {
      unstable_retry();
      return;
    }
    if (reset) {
      reset();
      return;
    }
    window.location.reload();
  };

  const isDev = process.env.NODE_ENV === "development";

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          background: "#f8fafc",
          color: "#0f172a",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            padding: "32px 28px",
            textAlign: "left",
          }}
        >
          <h1
            style={{
              margin: "0 0 12px",
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.02em",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              margin: "0 0 20px",
              fontSize: 14,
              lineHeight: 1.5,
              color: "#475569",
            }}
          >
            The app hit an unexpected error. You can try again, or go back to
            the home page.
          </p>

          {isDev && error?.message ? (
            <pre
              style={{
                margin: "0 0 16px",
                padding: 12,
                fontSize: 12,
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                background: "#fee2e2",
                color: "#7f1d1d",
                borderRadius: 8,
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              {error.message}
            </pre>
          ) : null}

          {error?.digest ? (
            <p
              style={{
                margin: "0 0 20px",
                fontSize: 12,
                color: "#64748b",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              Error ID: {error.digest}
            </p>
          ) : null}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={retry}
              style={{
                height: 36,
                padding: "0 14px",
                fontSize: 14,
                fontWeight: 500,
                borderRadius: 8,
                cursor: "pointer",
                color: "#fff",
                background: "#22388d",
                border: "none",
              }}
            >
              Try again
            </button>
            <a
              href="/"
              style={{
                display: "inline-flex",
                alignItems: "center",
                height: 36,
                padding: "0 14px",
                fontSize: 14,
                fontWeight: 500,
                borderRadius: 8,
                color: "#0f172a",
                background: "transparent",
                border: "1px solid #cbd5e1",
                textDecoration: "none",
              }}
            >
              Home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
